import { MaxDropLimits } from "helpers/types";
import { useStrategyReportValue } from "./useReportStorage";

const MAX_DROP_LIMITS_KEY = "max_drop_limits_strategy";
const DEFAULT_MAX_DROP = 20;

// Mirrors the stock report's rule, clamped to the magnitude the edit modal
// accepts so a derived limit is always one the user could have typed.
const deriveLimits = (stockDrop: number, oeaDrop: number): MaxDropLimits => {
  if (stockDrop < 0 && oeaDrop < 0) {
    const deepest = Math.floor(Math.min(stockDrop, oeaDrop));
    return { maxDrop: DEFAULT_MAX_DROP, negativeLimit: Math.min(deepest, -1) };
  }

  const widest = Math.ceil(Math.max(stockDrop, oeaDrop));
  return { maxDrop: Math.max(widest, 1), negativeLimit: -DEFAULT_MAX_DROP };
};

/** `symbol` is "" until a report has been calculated — a slot of its own. */
export const useMaxDropLimits = (
  symbol: string,
  stockDrop: number,
  oeaDrop: number,
): [MaxDropLimits, (limits: MaxDropLimits) => void] => {
  const [storedValue, setStoredValue] = useStrategyReportValue(
    MAX_DROP_LIMITS_KEY,
    "{}",
  );
  const saved: Record<string, MaxDropLimits> = JSON.parse(String(storedValue));

  // Both limits are written together — two setters would each save the entry
  // from their own closure and the second would drop the first.
  const setLimits = (limits: MaxDropLimits) =>
    setStoredValue(JSON.stringify({ ...saved, [symbol]: limits }));

  return [saved[symbol] ?? deriveLimits(stockDrop, oeaDrop), setLimits];
};
